import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/contexts/auth';
import { api } from '@/lib/api';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'magic' | 'password'>('magic');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (mode === 'magic') {
        await api.auth.requestMagicLink(email);
        setLinkSent(true);
      } else {
        await login(email, password);
        navigate({ to: '/dreams' });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (linkSent) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="card">
            <h1 className="text-2xl font-display font-bold text-white mb-3">Check your inbox</h1>
            <p className="text-dream-300 mb-2">
              If <span className="text-white">{email}</span> has an account, a sign-in link is on
              its way. It works once and expires in 15 minutes.
            </p>
            <button
              onClick={() => setLinkSent(false)}
              className="text-dream-300 hover:text-white text-sm mt-4"
            >
              Use a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-display font-bold text-white mb-2">Welcome Back</h1>
          <p className="text-dream-300">Sign in to continue your dream journey</p>
        </div>

        <form onSubmit={handleSubmit} className="card">
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-dream-200 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input"
                placeholder="you@example.com"
              />
            </div>

            {mode === 'password' && (
              <div>
                <label className="block text-sm font-medium text-dream-200 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="input"
                  placeholder="••••••••"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-3"
            >
              {isLoading
                ? mode === 'magic' ? 'Sending link...' : 'Signing in...'
                : mode === 'magic' ? 'Email me a sign-in link' : 'Sign In'}
            </button>

            <button
              type="button"
              onClick={() => {
                setMode(mode === 'magic' ? 'password' : 'magic');
                setError('');
              }}
              className="w-full text-sm text-dream-300 hover:text-white"
            >
              {mode === 'magic' ? 'Sign in with a password instead' : 'Email me a sign-in link instead'}
            </button>
          </div>
        </form>

        <p className="text-center text-dream-400 mt-6">
          Don't have an account?{' '}
          <Link to="/signup" className="text-dream-300 hover:text-white">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
